import { useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { useDebounce } from "react-use";

import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

import { employeeLabel, useEmployeeSearch } from "@/hooks/use-access-keys";
import type { MinimizedEmployee } from "@/hooks/use-access-keys";

interface EmployeeComboboxProps {
  id?: string;
  tenantId: string;
  value: MinimizedEmployee | null;
  onChange: (employee: MinimizedEmployee | null) => void;
  disabled?: boolean;
}

/** The minimized search excludes service accounts and departed employees. */
export function EmployeeCombobox({
  id,
  tenantId,
  value,
  onChange,
  disabled,
}: EmployeeComboboxProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  useDebounce(
    () => {
      setDebouncedSearch(search);
    },
    300,
    [search],
  );

  const {
    data: employees,
    isFetching,
    isError,
  } = useEmployeeSearch(tenantId, debouncedSearch, open);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className="w-full justify-between font-normal"
        >
          {value ? (
            <span className="truncate">
              {employeeLabel(value)}
              <span className="text-muted-foreground">
                {" "}
                · {value.work_email}
              </span>
            </span>
          ) : (
            <span className="text-muted-foreground">
              Search by name or email
            </span>
          )}
          <ChevronsUpDown className="ml-2 size-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] p-0"
        align="start"
      >
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Type a name or email…"
            value={search}
            maxLength={200}
            onValueChange={setSearch}
          />
          <CommandList className="max-h-[300px]">
            <CommandEmpty>
              {isFetching
                ? "Searching…"
                : isError
                  ? "The search failed; try again."
                  : "No matching person in this tenant."}
            </CommandEmpty>
            <CommandGroup>
              {(employees ?? []).map((employee) => (
                <CommandItem
                  key={employee.id}
                  value={employee.id}
                  onSelect={() => {
                    onChange(employee.id === value?.id ? null : employee);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 size-4",
                      value?.id === employee.id ? "opacity-100" : "opacity-0",
                    )}
                  />
                  <div className="flex min-w-0 flex-col">
                    <span className="truncate">{employeeLabel(employee)}</span>
                    <span className="text-muted-foreground truncate text-xs">
                      {employee.work_email}
                      {employee.job_title ? ` · ${employee.job_title}` : ""}
                    </span>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
